import { Component, ViewEncapsulation } from '@angular/core';
import { OnInit, Input } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AlertsService } from '../../services/alerts.service';
import { UserService } from '../../services/user.service';
import { ConfigService } from '../../services/config.service';
import { ViewerService } from '../../services/viewer.service';
import { MetadataService } from '../../services/metadata.service';
import { ProductService } from '../../services/product.service';
import { ConfirmComponent } from '../confirm/confirm.component';

import { PageDescription } from '../../models/viewer.interface';

import { formatString } from '../../utils/string-utils';

import dayjs from 'dayjs';

/**
 * Component to view documents and metadata side by side.
 */
@Component({
	selector: 'diff-viewer',
	templateUrl: './diff-viewer.component.html',
	styleUrl: './diff-viewer.component.css',
	encapsulation: ViewEncapsulation.None  // Unblock Bootstrap 3 table styles
})
export class DiffViewerComponent implements OnInit {
	@Input() products: any;

	LOCALIZATION: any;
	userPermissions: { [key: string]: any } = {};
	dateFormatDisplay: string = 'MM/DD/YY';
	mapViewEnabled: boolean = false;

	tracker = {
		left: 0,
		right: 1,
		leftPageIndex: -1,
		rightPageIndex: -1
	};

	leftImage: { [key: string]: any } = {};
	rightImage: { [key: string]: any } = {};
	allProductInfo: any[] = [];

	constructor(
		private ngbModal: NgbModal,
		private ngbmodalService: NgbActiveModal,
		private userService: UserService,
		private viewerService: ViewerService,
		private metadataService: MetadataService,
		private productService: ProductService,
		private configService: ConfigService,
		private alertsService: AlertsService
	) { }

	ngOnInit(): void {
		this.LOCALIZATION = this.userService.LOCALIZATION;
		this.userPermissions = this.userService.getUserPermissionsMap();

		let dateTypeProperty = this.userService.user.properties.awpropertyObjectList.find(function(x: any) { return x.name === "USER_INTERFACE_DATE_TYPE"; });
		this.dateFormatDisplay = dateTypeProperty?.value || 'MM/DD/YY';
		
		this.mapViewEnabled = this.configService.getConfig().esri.enabled;

		this.initViewer();
	}

	private initViewer(): void {
		this.initDiffSide(true);
		this.initDiffSide(false);
	}

	initDiffSide(isLeft: boolean): void {
		let productId = isLeft ? this.products[this.tracker.left].id : this.products[this.tracker.right].id;

		this.viewerService.processDocument(productId, null, null).then(
			(result: any) => {
				let documentInfo = result;
				let docPagesArr = documentInfo.documentData.documentPages;
				let options = {
					mapId: isLeft ? "leftMap" : "rightMap",
					page: (documentInfo.documentData.pages && documentInfo.documentData.pages > 0 && docPagesArr && docPagesArr.length > 0)
						? docPagesArr[0]
						: undefined
				};

				if (isLeft) {
					this.tracker.leftPageIndex = 0;

					Object.keys(this.leftImage).forEach((key: string) => delete this.leftImage[key]);

					this.leftImage['side'] = "left";
					this.leftImage['product'] = {
						id: productId,
						title: documentInfo.title
					};
					this.leftImage['pages'] = docPagesArr;
					this.leftImage['documentData'] = documentInfo.documentData;
					this.leftImage['fullRecordMetadata'] = documentInfo.fullRecordMetadata;

					// TODO do we need this? package seems to be explicitly for PDF
					this.leftImage['package'] = {
						packagePage: -1,
						page: -1,
						pages: []
					};

					this.processViewerItem(this.leftImage);

					// Update the object reference to trigger a DOM change
					this.leftImage = { ...this.leftImage };
				}
				else {
					this.tracker.rightPageIndex = 0;

					Object.keys(this.rightImage).forEach((key: string) => delete this.rightImage[key]);

					this.rightImage['side'] = "right";
					this.rightImage['product'] = {
						id: productId,
						title: documentInfo.title
					};
					this.rightImage['pages'] = docPagesArr;
					this.rightImage['documentData'] = documentInfo.documentData;
					this.rightImage['fullRecordMetadata'] = documentInfo.fullRecordMetadata;

					// TODO do we need this? package seems to be explicitly for PDF
					this.rightImage['package'] = {
						packagePage: -1,
						page: -1,
						pages: []
					};

					this.processViewerItem(this.rightImage);

					// Update the object reference to trigger a DOM change
					this.rightImage = { ...this.rightImage };
				}
			},
			(err: any) => {
				console.error("Failed to load document:", err);
			}
		);
	}

	private processViewerItem(viewerItem: any) {
		let pageUrl = null;
		let pageOverviewUrl = null;

		viewerItem.pageDescription = this.getPageDescription(viewerItem['documentData']);

		switch (viewerItem.pageDescription.type) {
			case "TEXT":
				viewerItem.pageDescription.currView = "text";

				// handle PDFs, emails, etc. differently here
				if ((viewerItem['documentData']['documentType'] === 'application/pdf')
					|| (viewerItem.pageDescription.mime && viewerItem.pageDescription.mime.toLowerCase().indexOf("pdf") > -1)
				) {
					viewerItem.pageDescription.currView = "pdf";
					// viewerURL is generated in the sub-component
					break;
				}

				let searchString = this.viewerService.buildDocIdSearchString();
				pageUrl = (viewerItem.page == null || viewerItem.page.pageNumber == null)
					? formatString(this.configService.getConfig().document.documentProviderHighlightURL, viewerItem.product.id, searchString)
					: viewerItem.page._overview;

				break;

			case "VIDEO":
				viewerItem.pageDescription.currView = "video";

				pageUrl = (viewerItem.page == null || viewerItem.page.pageNumber == null)
					? formatString(this.configService.getConfig().document.documentProviderURL, viewerItem.product.id)
					: viewerItem.page._overview;

				pageOverviewUrl = (viewerItem.page == null || viewerItem.page.pageNumber == null)
					? formatString(this.configService.getConfig().document.thumbnailSingularPageURL, viewerItem.product.id)
					: viewerItem.page._thumbs;

				break;

			case "IMAGE":
				pageUrl = (viewerItem.page == null || viewerItem.page.pageNumber == null)
					? formatString(this.configService.getConfig().document.documentProviderURL, viewerItem.product.id)
					: viewerItem.page._overview;

				if (viewerItem.pageDescription.mime === 'image/gif') {
					viewerItem.pageDescription.currView = "image-default";
				}
				else {
					viewerItem.pageDescription.currView = "image";
				}
				break;

			case "AUDIO":
				viewerItem.pageDescription.currView = "audio";

				pageUrl = (viewerItem.page == null || viewerItem.page.pageNumber == null)
					? formatString(this.configService.getConfig().document.documentProviderURL, viewerItem.product.id)
					: viewerItem.page._overview;

				pageOverviewUrl = (viewerItem.page == null || viewerItem.page.pageNumber == null)
					? formatString(this.configService.getConfig().document.thumbnailSingularPageURL, viewerItem.product.id)
					: viewerItem.page._thumbs;

				break;
				
			case "METADATA":
				viewerItem.pageDescription.currView = "metadata";

				break;

			case "VECTOR":
				// This shows a projected image on a map
				if (viewerItem.pageDescription.mime == "application/vnd.google-earth.kml+xml") {
					viewerItem.pageDescription.currView = "map";

					pageUrl = ((viewerItem.page == null || viewerItem.page.pageNumber == null)
						? formatString(this.configService.getConfig().document.documentProviderURL, viewerItem.product.id)
						: viewerItem.page._overview);

					viewerItem.pageDescription.type = viewerItem.pageDescription.mime;
				}

				break;

			case "ARCGIS_LAYER":
				// This shows a geo feature layer and dataset on a map
				if (!viewerItem['documentData']['arcGISQueryURL']) {
					viewerItem.pageDescription.currView = "metadata";
					break;
				}

				if (viewerItem['documentData']['arcGISQueryURL'].indexOf("aw-server") > -1) {
					viewerItem.pageDescription.currView = "arcgis";

					pageUrl = viewerItem['documentData']['arcGISQueryURL'];
				} else {
					// Opens an external third-party viewer. Requires CORS considerations.
					viewerItem.pageDescription.currView = "external_arcgis";
					pageUrl = `https://arcgis.com/home/webmap/viewer.html?url=${viewerItem['documentData']['arcGISQueryURL']}&source=sd`;
				}

				break;

			default:
				viewerItem.pageDescription.currView = "metadata";
		}

		viewerItem.viewerURL = this.viewerService.generateViewerURL(pageUrl);
		viewerItem.viewerOverviewURL = this.viewerService.generateViewerURL(pageOverviewUrl);

		this.processMetadata(viewerItem);

		console.log(`Product Id: ${viewerItem.product.id}, Title: ${viewerItem.product.title}`);
		console.log(`Current View: ${viewerItem.pageDescription.currView}`);
	}

	private getPageDescription(documentData: any): PageDescription {
		let type = "IMAGE";  // TODO Shouldn't we default to OTHER?
		let mime = documentData['documentType'];

		if (mime.toLowerCase().indexOf("image") > -1) {
			type = "IMAGE";
		} else if (mime.indexOf("text") > -1) {
			type = "TEXT";
		} else if (mime.toLowerCase().indexOf("video") > -1) {
			type = "VIDEO";
		} else if (mime.toLowerCase().indexOf("audio") > -1) {
			type = "AUDIO";
		} else if (mime.toLowerCase().indexOf("metadata") > -1) {
			type = "METADATA";
		} else if (documentData['arcGISQueryURL']) {
			type = "ARCGIS_LAYER";
		} else if (mime.toLowerCase().indexOf("vnd.google-earth.kml+xml") > -1) {
			type = "VECTOR";
		} else if (mime.toLowerCase().indexOf("pdf") > -1
			|| mime.toLowerCase().indexOf("msword") > -1
			|| mime.toLowerCase().indexOf("powerpoint") > -1
			|| mime.toLowerCase().indexOf("pptx") > -1
			|| mime.toLowerCase().indexOf("ntf") > -1
			|| mime.toLowerCase().indexOf("rtf") > -1
			|| mime.toLowerCase().indexOf("xml") > -1
			|| mime.toLowerCase().indexOf("ms-excel") > -1) {
			type = "TEXT";
		} else if (mime.toLowerCase().indexOf("octet-stream") > -1) {
			// Treat this specifically as OTHER since it is a generic mime type for binary files
			type = "OTHER";
		}
		else {
			if (documentData) {
				type = documentData['contentType'];
			} else {
				type = "IMAGE"; // TODO Shouldn't we default to OTHER?
			}
		}

		let pageDescription: PageDescription = {
			type: type,
			mime: mime,
			currView: 'metadata'
		}

		console.log(`Processing page; type = ${pageDescription.type}, mime = ${pageDescription.mime}`);

		return pageDescription;
	}

	private processMetadata(viewerItem: any) {
		let productHash = viewerItem['fullRecordMetadata'];
		let productInfo: { [key: string]: any } = {};
		let validFields = this.userService.user.properties.awfieldObjectList.filter((x: any) => { return x.isAvailable && x.isMetadata });

		for (let p = 0; p < validFields.length; p++) {
			let field = validFields[p];

			if (!productHash[field.name]) {
				continue;
			}

			productInfo[field.displayName] = {
				label: field.displayName,
				value: productHash[field.name],
				maxLength: (field.isLink ? -1 : 220),
				links: null,
				name: field.name
			};

			productInfo[field.displayName].cleanedValue = this.metadataService.cleanValue(productInfo[field.displayName].value);
			productInfo[field.displayName].isValidCollection =
				this.metadataService.isValidFieldCollectionValue(productInfo[field.displayName].name, productInfo[field.displayName].cleanedValue);

			if (field.type == "DATE") {
				productInfo[field.displayName].value = dayjs(productInfo[field.displayName].value).format(this.dateFormatDisplay);
			}

			if (field.name == "requires") {
				productInfo[field.displayName].warning = true;
			}

			if (field.isLink) {
				productInfo[field.displayName].links = this.metadataService.linksFromString(productHash[field.name], field.name);
			} else {
				if (productInfo[field.displayName].value) {
					productInfo[field.displayName].value = productInfo[field.displayName].value.replace(/; /g, ';').replace(/;/g, '; ');
				}
			}
		}

		viewerItem.productInfo = productInfo;

		this.mergeMetadata();
	}

	private mergeMetadata(): void {
		this.allProductInfo = [];

		const leftInfo = this.leftImage?.['productInfo'] || {};
		const rightInfo = this.rightImage?.['productInfo'] || {};

		const leftKeys = Object.keys(leftInfo).length;
		const rightKeys = Object.keys(rightInfo).length;

		const master = leftKeys < rightKeys ? rightInfo : leftInfo;

		const sortedKeys = Object.keys(master).sort();

		this.allProductInfo = sortedKeys.map((key: string) => ({
			field: key,
			left: leftInfo[key]?.value ?? '',
			right: rightInfo[key]?.value ?? ''
		}));
	}

	// Deprecated ?
	changePage(isLeft: boolean): void {
		if (isLeft) {
			this.leftImage['page'] = this.leftImage['pages'][this.tracker.leftPageIndex];
			this.processViewerItem(this.leftImage);
		} else {
			this.rightImage['page'] = this.rightImage['pages'][this.tracker.rightPageIndex];
			this.processViewerItem(this.rightImage);
		}
	}

	/**
	 * Deletes the product.
	 */
	deleteDocument(isLeft: boolean): void {
		const modalRef = this.ngbModal.open(ConfirmComponent, {
			size: 'sm'
		});

		modalRef.componentInstance.modal = {
			callerName: 'deleteDocument',
			title: this.LOCALIZATION.deleteDocumentHeader,
			message: this.LOCALIZATION.deleteDocumentMessage,
			type: 'danger'
		};

		modalRef.result.then(
			(payload: any) => {
				if (payload.result?.answer === 'ok') {
					let id = isLeft ? this.leftImage['product']['id'] : this.rightImage['product']['id'];
					this.productService.deleteDocuments([id]).then(
						(response: any) => {
							this.alertsService.success({
								message: this.LOCALIZATION.searchUpdatesConfirmationMessage,
								delay: 5000
							});
						},
						(err: any) => {
							console.error("Failed to delete product:", err);
							this.alertsService.error({
								message: this.LOCALIZATION.deleteDocumentError
							});
						}
					);
				}
			},
			(reason: any) => {
				console.log('Modal dismissed');
			}
		);
	}
	
	arcgis_onerror(): void {
		console.error("Failed to load the product");
	}

	/**
	 * Closes the modal.
	 */
	close(): void {
		this.ngbmodalService.dismiss();
	}
}